/**
 * MSR item 5: "Add my schedule to Google / Outlook" -- the rep's private, read-only calendar subscription link.
 *
 * The link is a secret address (calendar apps cannot sign in), so the panel:
 *   - never creates a link on page view ('get' only); the rep presses "Create my calendar link";
 *   - shows the link again later (copy, open in Google / Outlook / the phone's calendar app);
 *   - lets the rep make a new link (the old one stops working at once) or turn it off.
 * All of it goes through msr_calendar_feed_link (mig 562), which acts on the signed-in rep only.
 */

import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ConfirmDialog from "../ui/ConfirmDialog";
import { useToast } from "../ui/Toast";
import {
  msrCalendarFeedLink,
  msrCalendarFeedUrl,
  type MsrCalendarFeedAction,
  type MsrCalendarFeedLink,
} from "../../services/msrPortal.service";

const QUERY_KEY = ["msr-calendar-feed-link"];

const DONE_MESSAGE: Record<MsrCalendarFeedAction, string> = {
  get: "",
  create: "Calendar link created",
  rotate: "New calendar link made. The old one no longer works.",
  revoke: "Calendar link turned off",
};

const when = (iso: string | null | undefined): string =>
  iso
    ? new Date(iso).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "";

const MsrCalendarSubscribe: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState<"rotate" | "revoke" | null>(null);
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => msrCalendarFeedLink("get"),
    enabled: open,
    staleTime: 60_000,
  });

  const mut = useMutation({
    mutationFn: (action: MsrCalendarFeedAction) => msrCalendarFeedLink(action),
    onSuccess: (res: MsrCalendarFeedLink, action) => {
      queryClient.setQueryData(QUERY_KEY, res);
      showToast("success", DONE_MESSAGE[action]);
    },
    onError: (e: Error) => showToast("error", e.message),
  });

  const token = data?.status === "active" ? data.token : undefined;
  const httpsUrl = token ? msrCalendarFeedUrl(token) : "";
  const webcalUrl = httpsUrl.replace(/^https?:\/\//, "webcal://");
  const googleUrl = webcalUrl
    ? `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcalUrl)}`
    : "";
  const outlookUrl = httpsUrl
    ? `https://outlook.office.com/calendar/0/addfromweb?url=${encodeURIComponent(httpsUrl)}&name=${encodeURIComponent("USLS - My Schedule")}`
    : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(httpsUrl);
      showToast("success", "Link copied");
    } catch {
      showToast("error", "Could not copy. Select the link and copy it.");
    }
  };

  return (
    <section
      className="rounded-lg border border-gray-200 bg-white"
      data-testid="msr-calendar-subscribe"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex min-h-[44px] w-full items-center justify-between px-4 py-3 text-left"
        data-testid="msr-calendar-subscribe-toggle"
      >
        <span className="text-sm font-semibold text-gray-900">
          Add my schedule to Google or Outlook
        </span>
        <span className="text-xs text-primary-700">
          {open ? "Hide" : "Set up"}
        </span>
      </button>

      {open && (
        <div className="space-y-3 border-t border-gray-100 px-4 py-3 text-sm text-gray-700">
          <p>
            Your event days appear in your own calendar app and stay up to date
            by themselves. It is read-only: change things here in the CRM.
          </p>

          {isLoading && <p className="text-gray-500">Loading...</p>}
          {error && <p className="text-red-700">{(error as Error).message}</p>}

          {data && data.status !== "active" && (
            <div className="space-y-2">
              {data.status === "unreadable" && (
                <p className="text-amber-800">
                  Your link can't be shown. Make a new one (the old one stops
                  working).
                </p>
              )}
              <button
                type="button"
                disabled={mut.isPending}
                onClick={() =>
                  mut.mutate(data.status === "unreadable" ? "rotate" : "create")
                }
                className="min-h-[44px] rounded-md bg-primary-600 px-4 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                data-testid="msr-calendar-create"
              >
                {data.status === "unreadable"
                  ? "Make a new link"
                  : "Create my calendar link"}
              </button>
            </div>
          )}

          {token && (
            <div className="space-y-3" data-testid="msr-calendar-active">
              <div>
                <label
                  htmlFor="msr-calendar-url"
                  className="mb-1 block text-xs font-medium text-gray-600"
                >
                  Your private calendar link
                </label>
                <div className="flex gap-2">
                  <input
                    id="msr-calendar-url"
                    readOnly
                    value={httpsUrl}
                    onFocus={(e) => e.currentTarget.select()}
                    className="min-w-0 flex-1 rounded-md border border-gray-300 bg-gray-50 px-2 py-2 font-mono text-xs"
                    data-testid="msr-calendar-url"
                  />
                  <button
                    type="button"
                    onClick={copy}
                    className="min-h-[40px] shrink-0 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium hover:bg-gray-50"
                    data-testid="msr-calendar-copy"
                  >
                    Copy
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <a
                  href={googleUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex min-h-[40px] items-center rounded-md bg-gray-800 px-3 text-sm font-medium text-white hover:bg-gray-900"
                  data-testid="msr-calendar-google"
                >
                  Add to Google Calendar
                </a>
                <a
                  href={outlookUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex min-h-[40px] items-center rounded-md bg-gray-800 px-3 text-sm font-medium text-white hover:bg-gray-900"
                  data-testid="msr-calendar-outlook"
                >
                  Add to Outlook
                </a>
                <a
                  href={webcalUrl}
                  className="inline-flex min-h-[40px] items-center rounded-md border border-gray-300 px-3 text-sm font-medium hover:bg-gray-50"
                  data-testid="msr-calendar-webcal"
                >
                  Open in my phone's calendar
                </a>
              </div>

              <ul className="list-disc space-y-1 pl-5 text-xs text-gray-600">
                <li>
                  <strong>Google Calendar</strong> (computer): Other calendars,
                  then <em>+</em>, then <em>From URL</em>, paste the link.
                  Google checks for changes about every 8 to 24 hours, so a
                  change can take up to a day to show there.
                </li>
                <li>
                  <strong>Outlook</strong>: Add calendar, then{" "}
                  <em>Subscribe from web</em>, paste the link.
                </li>
                <li>
                  <strong>iPhone</strong>: tap{" "}
                  <em>Open in my phone's calendar</em> and choose Subscribe.
                </li>
              </ul>

              <p className="rounded-md bg-amber-50 p-2 text-xs text-amber-900">
                Keep this link private: anyone who has it can see your event
                days (never member details). If it gets out, make a new link.
              </p>

              <p
                className="text-xs text-gray-500"
                data-testid="msr-calendar-last"
              >
                {data?.last_fetched_at
                  ? `Last checked by a calendar app: ${when(data.last_fetched_at)}`
                  : "Not checked by a calendar app yet."}
              </p>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={mut.isPending}
                  onClick={() => setConfirm("rotate")}
                  className="min-h-[40px] rounded-md border border-gray-300 px-3 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
                  data-testid="msr-calendar-rotate"
                >
                  Make a new link
                </button>
                <button
                  type="button"
                  disabled={mut.isPending}
                  onClick={() => setConfirm("revoke")}
                  className="min-h-[40px] rounded-md border border-red-200 px-3 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                  data-testid="msr-calendar-revoke"
                >
                  Turn off
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={confirm !== null}
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          const action = confirm;
          setConfirm(null);
          if (action) {
            mut.mutate(action);
          }
        }}
        title={
          confirm === "revoke"
            ? "Turn off your calendar link?"
            : "Make a new calendar link?"
        }
        message={
          confirm === "revoke"
            ? "Your calendar apps stop getting updates and the link stops working. You can create a new one later."
            : "The current link stops working right away. Add the new link to your calendar apps again."
        }
        confirmText={confirm === "revoke" ? "Turn off" : "Make a new link"}
        variant={confirm === "revoke" ? "danger" : "warning"}
        isLoading={mut.isPending}
      />
    </section>
  );
};

export default MsrCalendarSubscribe;
